import { Inbox, RefreshCw } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type EmailOutboxRow,
  useEmailOutbox,
  useEmailOutboxDetail,
} from "@/hooks/use-email-outbox";
import { formatDateTime } from "@/lib/dates";

// Outbox status → pill tone. Terminal-good = success, in-flight = info,
// failures/bounces = danger, freshly queued = neutral.
const STATUS_TONE: Record<string, StatusTone> = {
  pending: "neutral",
  queued: "info",
  sending: "info",
  sent: "success",
  delivered: "success",
  bounced: "danger",
  complained: "danger",
  failed: "danger",
};

function toneFor(status: string): StatusTone {
  return STATUS_TONE[status] ?? "neutral";
}

export function EmailOutbox() {
  const { data: rows = [], isLoading, isError, error, refetch, isFetching } = useEmailOutbox();
  const [openId, setOpenId] = useState<string | null>(null);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the outbox"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataToolbar
        actions={
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Status" },
          { label: "To" },
          { label: "Subject" },
          { label: "Attempts", className: "text-right" },
          { label: "Created" },
          { label: "Sent" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Inbox}
            title="No emails yet"
            description="Sent emails will appear here once the app dispatches them."
          />
        }
      >
        {rows.map((row) => (
          <OutboxRow key={row.id} row={row} onOpen={() => setOpenId(row.id)} />
        ))}
      </DataTable>

      <MessageDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function OutboxRow({ row, onOpen }: { row: EmailOutboxRow; onOpen: () => void }) {
  return (
    <tr
      className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
      onClick={onOpen}
    >
      <td className="px-4 py-3">
        <StatusPill tone={toneFor(row.status)} label={row.status} />
      </td>
      <td className="px-4 py-3">
        <span className="block max-w-[16rem] truncate" title={row.toAddresses.join(", ")}>
          {row.toAddresses.join(", ")}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="block max-w-[20rem] truncate" title={row.subject}>
          {row.subject || <span className="text-muted-foreground">(no subject)</span>}
        </span>
        {row.templateName && (
          <span className="block text-xs text-muted-foreground">{row.templateName}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{row.attempts}</td>
      <td className="px-4 py-3 text-muted-foreground">{formatDateTime(row.createdAt)}</td>
      <td className="px-4 py-3 text-muted-foreground">{formatDateTime(row.sentAt)}</td>
    </tr>
  );
}

/** Read the sent message: metadata + the rendered body (HTML in a sandboxed iframe). */
function MessageDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading, isError } = useEmailOutboxDetail(id);

  return (
    <Dialog open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{data?.subject || "Message"}</DialogTitle>
          {data && (
            <DialogDescription>
              To {data.toAddresses.join(", ")} · from {data.fromAddress}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading && <LoadingRows />}
        {isError && <p className="text-sm text-danger-foreground">Failed to load the message.</p>}

        {data && (
          <div className="space-y-4">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <StatusPill tone={toneFor(data.status)} label={data.status} />
              </dd>
              <dt className="text-muted-foreground">Sent</dt>
              <dd>{formatDateTime(data.sentAt)}</dd>
              <dt className="text-muted-foreground">Delivered</dt>
              <dd>{formatDateTime(data.deliveredAt)}</dd>
              {data.messageId && (
                <>
                  <dt className="text-muted-foreground">Message ID</dt>
                  <dd className="break-all font-mono text-xs">{data.messageId}</dd>
                </>
              )}
              {data.lastError && (
                <>
                  <dt className="text-muted-foreground">Last error</dt>
                  <dd className="text-danger-foreground">{data.lastError}</dd>
                </>
              )}
            </dl>

            {/* Email HTML is hostile content — render it isolated in a sandboxed
                iframe (no scripts, no same-origin). Falls back to the text body. */}
            {data.bodyHtml ? (
              <iframe
                title="Email body"
                sandbox=""
                srcDoc={data.bodyHtml}
                className="h-96 w-full rounded-md border bg-white"
              />
            ) : (
              <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap">
                {data.bodyText || "(no body)"}
              </pre>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
